import { useState, type ReactNode } from "react";
import { Badge, Button, Card, EmptyState, Icon } from "@/shared/ui";
import { AiErrorMessage } from "./AiErrorMessage";

const omit = <V,>(rec: Record<string, V>, k: string) => {
  const next = { ...rec };
  delete next[k];
  return next;
};

export interface AiDraftReviewProps<T> {
  /** Generated drafts still waiting for a decision (the caller removes accepted / discarded ones). */
  drafts: T[];
  /** Stable key per draft. */
  draftKey: (draft: T, index: number) => string;
  /** Read-only view of a draft. */
  renderDraft: (draft: T) => ReactNode;
  /** Editor for "Sửa trước khi lưu"; without it drafts can only be accepted as is. */
  renderEditor?: (draft: T, onChange: (next: T) => void) => ReactNode;
  /** Saves a draft (with the edits, if any). A returned promise shows progress on that draft. */
  onAccept: (draft: T) => unknown;
  onDiscard: (draft: T) => void;
  /** Adds an "Accept all" action (drafts being edited are accepted with their edits). */
  onAcceptAll?: (drafts: T[]) => unknown;
  title?: ReactNode;
  acceptLabel?: string;
  emptyText?: ReactNode;
}

/**
 * Review list for AI-generated drafts: each one is accepted, discarded or
 * edited before saving. Nothing generated is saved without a click.
 */
export function AiDraftReview<T>({
  drafts, draftKey, renderDraft, renderEditor, onAccept, onDiscard, onAcceptAll,
  title = "Bản nháp do AI tạo", acceptLabel = "Lưu", emptyText = "Không còn bản nháp nào",
}: AiDraftReviewProps<T>) {
  const [edits, setEdits] = useState<Record<string, T>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [busyAll, setBusyAll] = useState(false);
  const [errors, setErrors] = useState<Record<string, unknown>>({});
  const keyed = drafts.map((d, i) => ({ key: draftKey(d, i), draft: d }));
  const current = (k: string, d: T) => edits[k] ?? d;
  const forget = (k: string) => setEdits((e) => omit(e, k));

  const accept = async (k: string, d: T) => {
    setBusy((b) => ({ ...b, [k]: true }));
    setErrors((e) => omit(e, k));
    try {
      await onAccept(current(k, d));
      forget(k);
    } catch (e) {
      setErrors((all) => ({ ...all, [k]: e }));
    } finally {
      setBusy((b) => omit(b, k));
    }
  };
  const acceptAll = async () => {
    setBusyAll(true);
    setErrors({});
    try {
      await onAcceptAll?.(keyed.map(({ key, draft }) => current(key, draft)));
      setEdits({});
    } catch (e) {
      setErrors({ "*": e });
    } finally {
      setBusyAll(false);
    }
  };

  return (
    <Card title={title} icon="sparkles" subtitle="Nội dung do AI tạo, có thể chưa chính xác. Kiểm tra trước khi lưu."
      actions={onAcceptAll && drafts.length > 1
        ? <Button size="sm" variant="primary" icon="check" loading={busyAll} onClick={() => void acceptAll()}>Lưu tất cả ({drafts.length})</Button>
        : undefined}>
      <AiErrorMessage error={errors["*"]} />
      {!drafts.length ? <EmptyState compact icon="sparkles" title={emptyText} /> : (
        <ul className="ai-drafts">
          {keyed.map(({ key, draft }, i) => {
            const editing = key in edits;
            return (
              <li key={key} className="ai-draft" aria-label={`Bản nháp ${i + 1}`}>
                <div className="row gap-sm">
                  <Badge tone="violet" icon="sparkles">Bản nháp {i + 1}</Badge>
                  {editing && <Badge tone="warning" icon="pencil">Đang sửa</Badge>}
                </div>
                <div className="ai-draft-body">
                  {editing && renderEditor ? renderEditor(edits[key] as T, (next) => setEdits((e) => ({ ...e, [key]: next }))) : renderDraft(draft)}
                </div>
                <AiErrorMessage error={errors[key]} />
                <div className="row gap-sm justify-end">
                  <Button size="sm" variant="ghost" icon="x" disabled={busy[key]} onClick={() => { forget(key); setErrors((e) => omit(e, key)); onDiscard(draft); }}>Bỏ</Button>
                  {renderEditor && (editing
                    ? <Button size="sm" variant="ghost" icon="refresh" disabled={busy[key]} onClick={() => forget(key)}>Huỷ sửa</Button>
                    : <Button size="sm" variant="ghost" icon="pencil" onClick={() => setEdits((e) => ({ ...e, [key]: draft }))}>Sửa trước khi lưu</Button>)}
                  <Button size="sm" variant="primary" icon="check" loading={busy[key]} onClick={() => void accept(key, draft)}>{acceptLabel}</Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <p className="muted xsmall row-nowrap gap-2xs mb-0 ai-draft-note"><Icon name="info" size={13} />Bản nháp chưa lưu sẽ mất khi rời trang.</p>
    </Card>
  );
}
